import { useEffect, useState } from 'react';
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineX
} from 'react-icons/hi';



export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  isOpen: boolean;
  onClose: () => void;
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
}


function Toast({
  isOpen,
  onClose,
  type = 'info',
  title,
  message,
  duration = 4000
}: ToastProps): JSX.Element | null {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose]);

  if (!isOpen) return null;

  const getIconAndColor = () => {
    switch (type) {
      case 'success':
        return {
          icon: <HiOutlineCheckCircle />,
          color: 'var(--secondary)',
          bgColor: 'rgba(122, 185, 0, 0.12)',
          borderColor: 'var(--secondary)'
        };
      case 'error':
        return {
          icon: <HiOutlineXCircle />,
          color: 'var(--accent-pink)',
          bgColor: 'rgba(255, 107, 157, 0.12)',
          borderColor: 'var(--accent-pink)'
        };
      case 'warning':
        return {
          icon: <HiOutlineExclamationCircle />,
          color: 'var(--accent-orange)',
          bgColor: 'rgba(255, 159, 67, 0.12)',
          borderColor: 'var(--accent-orange)'
        };
      default:
        return {
          icon: <HiOutlineInformationCircle />,
          color: 'var(--accent-cyan)',
          bgColor: 'rgba(0, 212, 255, 0.12)',
          borderColor: 'var(--accent-cyan)'
        };
    }
  };

  const { icon, color, bgColor, borderColor } = getIconAndColor();

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        animation: isVisible ? 'slideInRight 0.3s ease' : 'slideOutRight 0.3s ease',
        maxWidth: '400px',
        width: '100%'
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          border: `1px solid ${borderColor}`,
          borderLeft: `4px solid ${borderColor}`,
          padding: '1rem 1.25rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: 'var(--radius-full)',
            background: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            fontSize: '1.25rem',
            flexShrink: 0
          }}
        >
          {icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <h4 style={{
              margin: '0 0 4px',
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}>
              {title}
            </h4>
          )}
          <p style={{
            margin: 0,
            fontSize: '0.875rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.5
          }}>
            {message}
          </p>
        </div>

        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: '4px',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <HiOutlineX style={{ fontSize: '1rem' }} />
        </button>
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideOutRight {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
}

export default Toast;